import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, Auftrag, Rechnung, Unternehmen } from "@kundeninfo/api-client";
import { Modal, ModalFuss, darfAbbrechen } from "@kundeninfo/ui";
import { Hinweis } from "@kundeninfo/ui/me-design";
import { AuftragDetail, auftragFormularFelder, auftragVorschauWerte,
         useAuftraegeUmfeld } from "../components/AuftragDetail.js";
import type { AuftragDetailClient } from "../components/AuftragDetail.js";
import { useAuftragAnlegen } from "../components/auftragAnlegen.js";
import type { Vorbelegung } from "../components/auftragAnlegen.js";
import { apiMeldung, entwuerfeGleich } from "../components/vorgangsdetail.js";
import { KiKnopf } from "../components/meHuellen.js";
import "./KundeAuftraege.css";

// Die Vorbelegung aus einem angenommenen Angebot gehört seit Fixrunde 1 zum Anlege-Hook
// (der Bereich benutzt beides); der Reiter reicht seinen Namen weiter, damit KundenAkte und
// App ihn wie bisher von hier importieren.
export type { Vorbelegung };

/** Nur die Klientenmethoden, die dieser Reiter wirklich braucht — so lässt er sich im Test
 *  mit einer Handvoll vi.fn zeichnen (Muster aus KundeAngebote.tsx). Der grössere Teil davon
 *  gehört seit dem Bausteinschnitt (Bereichs-Spalten, Etappe 1, Aufgabe 2) dem Detail
 *  (`AuftragDetailClient`); der Reiter selbst legt nur noch die LISTE und das Anlegen dazu. */
export type AuftraegeClient = AuftragDetailClient
  & Pick<ApiClient, "listSalesOrders" | "createSalesOrder" | "getSalesOrder">;

/** Die Verdrahtung, die von App über KundenAkte hierher durchgereicht wird (erst in T9). */
export type AuftraegeUmfeld = {
  client: AuftraegeClient;
  /** Die Unternehmen, zu denen der Kunde eine Beziehung hat — die EINZIGEN, die beim Anlegen
   *  wählbar sind (Ruling: „nur Unternehmen mit Kundenbeziehung"). KundenAkte filtert sie in T9
   *  aus `kunde.relations`; ein Auftrag verweist immer auf eines von ihnen, sodass hier auch die
   *  Kürzel-Auflösung bestehender Aufträge greift. */
  companies: Unternehmen[];
  /** Optional: die Vorbelegung aus einem Angebot (Ruling P3A-B). Ohne sie ist es die normale
   *  unabhängige Auftragsanlage. */
  vorbelegung?: Vorbelegung | null;
  /** Der Auftrag, den ein Sprung von aussen aufschlagen soll: Der Reiter öffnet ihn, sobald
   *  seine Liste steht, statt den Bearbeiter den eben angeklickten Vorgang ein zweites Mal
   *  suchen zu lassen. Muster und Begründung wie bei `oeffneRechnungId` am Rechnungen-
   *  Reiter.
   *
   *  Seit Etappe 1, Aufgabe 4 kommt KEIN Sprung mehr von aussen: Die globale Auftragsliste
   *  ist die Objektliste des Auftrags-BEREICHS (views/BereichAuftraege.tsx). Der Weg bleibt
   *  trotzdem hier stehen — dieselbe Begründung wie am Angebote-Reiter. */
  oeffneAuftragId?: string | null;
  /** Die Auftrag→Rechnung-Naht (Paket 3b-2) — die Ableitung selbst geschieht im Baustein
   *  (components/AuftragDetail.tsx), hier wird nur der fertige Entwurf weitergereicht. */
  onRechnungAusAuftrag?: (rechnung: Rechnung) => void;
  /** Ist die Sitzung ADMIN? Reicht bis in den Kasten „Abrechnungsquellen" — dort darf ein
   *  ADMIN an nicht aktiven Aufträgen lösen (Entscheid Dirk 29.09.2026, Punkt 7).
   *  Weglassbar — dann nicht. */
  istAdmin?: boolean | undefined;
};

export type KundeAuftraegeProps = AuftraegeUmfeld & {
  kundeId: string;
  /** Der Firmenname des Kunden — allein für die Platzhalter-Vorschau (`#{kunde.name}`).
   *  Weglassbar: Fehlt er, bleibt das Token in der Vorschau sichtbar stehen (wie am
   *  Angebote-Reiter). */
  kundeName?: string;
};

/**
 * Der Aufträge-Reiter: Liste (neueste oben), Anlegen mit companyId und Laufzeit (auch aus
 * einem Angebot vorbelegt) und der Sprung von aussen auf einen Vorgang. Das DETAIL eines
 * Auftrags — Kopfzeile, Statusaktionen, Bearbeiten samt Positionen, „Als PDF" und die
 * Auftrag→Rechnung-Naht — zeichnet seit dem Bausteinschnitt (Bereichs-Spalten, Etappe 1,
 * Aufgabe 2) `components/AuftragDetail.tsx`. Denselben Baustein zeichnet seit Aufgabe 4 der
 * Auftrags-BEREICH (views/BereichAuftraege.tsx).
 *
 * Eigene Wurzelklasse `.kauf` (NICHT `.ka` teilen — Kollisions-Lehre 01.09., wie in
 * KundeAngebote/KundeNotizen).
 */
export function KundeAuftraege(p: KundeAuftraegeProps): ReactElement {
  const { client, kundeId, companies, vorbelegung } = p;

  const [auftraege, setAuftraege] = useState<Auftrag[] | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);

  const [neuOffen, setNeuOffen] = useState(vorbelegung != null);
  /** Die Kennung des gerade bearbeiteten Vorgangs — genau einer je Ansicht. */
  const [bearbeitetId, setBearbeitetId] = useState<string | null>(null);

  /** Die Auswahllisten lädt der REITER, einmal für alle Zeilen — und reicht sie an jedes
   *  Detail durch (siehe useAuftraegeUmfeld). */
  const umfeld = useAuftraegeUmfeld(client, kundeId);

  /** Lädt die Liste und GIBT sie zurück: Das Anlegen braucht den frischen Stand, um den
   *  gerade angelegten Auftrag gleich zum Bearbeiten zu öffnen (siehe useAuftragAnlegen). */
  const laden = useCallback(async (): Promise<Auftrag[]> => {
    try {
      const { items } = await client.listSalesOrders(kundeId);
      setAuftraege(items);
      setFehler(null);
      return items;
    } catch (e) {
      setFehler(apiMeldung(e, "Die Aufträge konnten nicht geladen werden."));
      return [];
    }
  }, [client, kundeId]);

  /** Das ANLEGEN führt der gemeinsame Hook (components/auftragAnlegen.ts) — derselbe, den
   *  der Auftrags-Bereich benutzt, samt der Vorbelegung aus einem Angebot. */
  const anlage = useAuftragAnlegen({
    client, kundeId, companies, vorbelegung, feldPraefix: "kauf-neu",
    onSchliessen: () => { schliessen(); },
    onNeuLaden: laden,
    onBearbeiten: (a, meldung) => { bearbeitenOeffnen(a, meldung); },
  });
  const { entwurf, setEntwurf, vorbelegtOfferId } = anlage;

  useEffect(() => { void laden(); }, [laden]);

  const formularOffen = neuOffen || bearbeitetId !== null;

  function schliessen(): void {
    setNeuOffen(false);
    setBearbeitetId(null);
    anlage.zuruecksetzen();
  }

  /**
   * Der EINE Ausgang der Anlage neben dem Anlegen (Hausregel „striktes Modalpanel",
   * 06.09.2026): kein Ausgang unter laufendem Schreiben, Rückfrage, sobald etwas im
   * Formular steht, das der Ausgangsstand nicht hatte. Die Vergleichsgrundlage führt der
   * Anlege-Hook (`anlage.ausgang`) — bei einer Anlage aus einem Angebot ist das der
   * VORBELEGTE Stand, nicht der leere.
   */
  async function anlageAbbrechen(): Promise<void> {
    if (anlage.speichert) return;
    if (!(await darfAbbrechen(!entwuerfeGleich(entwurf, anlage.ausgang)))) return;
    schliessen();
  }

  function neuOeffnen(): void {
    anlage.zuruecksetzen();
    setHinweis(null);
    setBearbeitetId(null);
    setNeuOffen(true);
  }

  /**
   * Der Sprung von aussen auf einen Vorgang — dieselbe Bauart und dieselbe Begründung wie
   * am Angebote-Reiter: genau EINMAL (`sprungErledigt`), sonst risse ein späteres Neuladen
   * der Liste das Formular auf den längst verlassenen Vorgang zurück; eine unbekannte
   * Kennung tut schlicht nichts. `bearbeitenOeffnen` steht bewusst nicht in den
   * Abhängigkeiten (wird bei jedem Zeichnen neu gebaut, setzt nur Zustand).
   */
  const sprungErledigt = useRef(false);
  useEffect(() => {
    const id = p.oeffneAuftragId;
    if (id === undefined || id === null || auftraege === null) return;
    if (sprungErledigt.current) return;
    const treffer = auftraege.find((a) => a.id === id);
    if (treffer === undefined) return;
    sprungErledigt.current = true;
    bearbeitenOeffnen(treffer, "Aus der Auftragssuche geöffnet.");
  }, [p.oeffneAuftragId, auftraege]);

  function bearbeitenOeffnen(a: Auftrag, meldung: string | null = null): void {
    // `zuruecksetzen` löscht auch die Herkunft: Die gehört zur ANLAGE; stünde sie beim
    // Bearbeiten noch, zeigte die Maske den Vorbelegungs-Hinweis an einem längst angelegten
    // Auftrag.
    anlage.zuruecksetzen();
    setHinweis(meldung);
    setNeuOffen(false);
    setBearbeitetId(a.id);
  }

  /** Der Satz eines Vorgangs, den der Baustein weitergedreht hat (Version/Betrag aus dem
   *  Positions-Editor), zurück in die Liste. */
  function fortschreiben(neu: Auftrag): void {
    setAuftraege((alle) => (alle === null
      ? alle : alle.map((a) => (a.id === neu.id ? neu : a))));
  }

  // ISO-Zeitstempel sortieren lexikografisch = chronologisch → neueste zuerst.
  const sortiert = [...(auftraege ?? [])].sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  return (
    <div className="kauf">
      <div className="kauf__kopf">
        <h2 className="kauf__titel">Aufträge</h2>
        <KiKnopf art="primaer" className="kauf__primaer" onClick={neuOeffnen}
          deaktiviert={formularOffen}>
          Neuer Auftrag
        </KiKnopf>
      </div>

      {companies.length === 0 && (
        <Hinweis art="info">
          Für diesen Kunden besteht noch keine Unternehmensbeziehung — ohne sie lässt sich kein
          Auftrag anlegen.
        </Hinweis>
      )}

      {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
      {hinweis !== null && <Hinweis art="info">{hinweis}</Hinweis>}

      {neuOffen && (
        <Modal breite="weit" abbrechenGesperrt={anlage.speichert}
          titel={vorbelegtOfferId !== null ? "Auftrag aus Angebot" : "Neuer Auftrag"}
          onAbbrechen={anlageAbbrechen}>
          {/* Der Abschnitt behält seinen Namen: Er ist die Maske IM Panel, und Tests wie
              Screenreader finden sie weiter unter ihrem Namen. */}
          <section className="kauf__formular"
            aria-label={vorbelegtOfferId !== null ? "Auftrag aus Angebot" : "Neuer Auftrag"}>
            {vorbelegtOfferId !== null && (
              <Hinweis art="info">
                Vorbelegt aus einem angenommenen Angebot — die Laufzeit noch ergänzen.
              </Hinweis>
            )}
            {/* Ohne Positionsblock: Am noch nicht angelegten Auftrag gibt es keinen
                Vorgang, an dem Positionen hängen könnten — aus einem Angebot abgeleitet
                bringt sie der Server mit, und bearbeitet werden sie danach im
                Bearbeiten-Panel. */}
            {auftragFormularFelder({
              praefix: "kauf-neu", mitUnternehmen: true, companies, umfeld, entwurf,
              setEntwurf, hatPositionen: false, feldFehler: anlage.feldFehler,
              fehlerJeFeld: anlage.fehlerJeFeld, formular: { laeuft: anlage.speichert },
              kopf: anlage.kopf,
              // Festlegung Z10: aus einem Angebot abgeleitet, ist die Periodenregel
              // gesperrt — sie kommt von dort, nicht aus einer zweiten Wahl hier.
              periodBasisGesperrt: vorbelegtOfferId !== null,
              vorschauWerte: auftragVorschauWerte({
                entwurf, companies, kontakte: umfeld.kontakte, kundeName: p.kundeName,
              }),
            })}
          </section>

          <ModalFuss speichernText="Anlegen" speichernGesperrt={anlage.speichert}
            abbrechenGesperrt={anlage.speichert}
            onSpeichern={() => { void anlage.anlegen(); }} onAbbrechen={anlageAbbrechen} />
        </Modal>
      )}

      {auftraege === null && fehler === null && <p className="kauf__hinweis">Lädt …</p>}

      {auftraege !== null && sortiert.length === 0 && (
        <p className="kauf__leer">Für diesen Kunden ist noch kein Auftrag erfasst.</p>
      )}

      {sortiert.length > 0 && (
        <ul className="kauf__liste">
          {sortiert.map((a) => (
            <li key={a.id} className="kauf__eintrag">
              <AuftragDetail
                client={client} kundeId={kundeId} auftrag={a} companies={companies}
                umfeld={umfeld} bearbeitung={bearbeitetId === a.id}
                gesperrt={formularOffen} kundeName={p.kundeName}
                istAdmin={p.istAdmin === true}
                onBearbeiten={() => bearbeitenOeffnen(a)}
                onSchliessen={schliessen}
                onNeuLaden={laden}
                onHinweis={setHinweis}
                onFortschreiben={fortschreiben}
                onRechnungAusAuftrag={p.onRechnungAusAuftrag} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
